"use client"

import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { fetchApplicantListAsync, selectApplicantListByJob } from "./applicantListByJobSlice"
import {
  Avatar,
  Tooltip,
  Box,
  Flex,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Badge,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react"
import { updateJobStatusAsync } from "../../components/Candidate/CandidateSlice/Basic/CandidateSlice"
import Modal from "../../components/Modal"
import * as ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  CheckBadgeIcon,
  ClockIcon,
  DocumentTextIcon,
  CakeIcon,
  UserIcon,
  BriefcaseIcon,
  LockClosedIcon,
  LockOpenIcon,
  EyeIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { format, parseISO } from "date-fns"
import { UnlockCandidateFromJobApplyAsync } from "../UnlockCandidateFromJobApply/UnlockCandidateFromJobApplySlice"
import { fetchEmployerCandidateJobApplyExportbyJobUuidAsync } from "../../employerDataExports/employerCandidateJobApplyListByJobUuidExport/employerCandidateJobApplyListByJobUuidExportSlice"
import { faFileExcel } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const STATUS_STYLES = {
  applied: { badgeBg: "bg-yellow-50", badgeText: "text-yellow-600" },
  shortlist: { badgeBg: "bg-green-50", badgeText: "text-green-600" },
  interview_scheduled: { badgeBg: "bg-blue-50", badgeText: "text-blue-600" },
  rejected: { badgeBg: "bg-red-50", badgeText: "text-red-600" },
  hired: { badgeBg: "bg-purple-50", badgeText: "text-purple-600" },
  default: { badgeBg: "bg-gray-50", badgeText: "text-gray-600" },
}

function statusStyles(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.default
}

function toStr(val) {
  if (val === null || val === undefined) return ""
  try {
    return String(val)
  } catch {
    return ""
  }
}

function ApplicantListByJob() {
  const dispatch = useDispatch()
  const { jobId } = useParams()

  // Use selector with jobId if supported, otherwise fallback
  const applicants = useSelector((state) => {
    try {
      return selectApplicantListByJob(state, jobId)
    } catch {
      return selectApplicantListByJob(state)
    }
  })

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState("")
  const [sortBy, setSortBy] = useState("")
  const [unlockStatus, setUnlockStatus] = useState({})
  const [isExporting, setIsExporting] = useState(false)
  const [query, setQuery] = useState("")
  const toast = useToast()

  const handleOpenModal = (candidate) => {
    setSelectedCandidate(candidate)
    setIsModalVisible(true)
  }

  const handleApplicantClick = (candidate) => {
    if (!candidate?.candidate_uuid) {
      toast({
        title: "Missing profile reference",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      })
      return
    }
    window.open(`/employer-dashboard/show-candidate/${candidate.candidate_uuid}`, "_blank")
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedCandidate(null)
    if (jobId) dispatch(fetchApplicantListAsync(jobId))
  }

  const handleUnlockCandidate = async (uuid) => {
    if (!uuid) return
    setUnlockStatus((prev) => ({ ...prev, [uuid]: "loading" }))
    try {
      await dispatch(UnlockCandidateFromJobApplyAsync({ uuid })).unwrap()
      setUnlockStatus((prev) => ({ ...prev, [uuid]: "success" }))
      toast({ title: "Candidate unlocked", status: "success", duration: 2000, isClosable: true, position: "top-right" })
      if (jobId) dispatch(fetchApplicantListAsync(jobId))
    } catch (error) {
      setUnlockStatus((prev) => ({ ...prev, [uuid]: "error" }))
      toast({
        title: "Unlock failed",
        description: error?.message || "Please try again later",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  useEffect(() => {
    if (jobId) {
      setIsLoading(true)
      dispatch(fetchApplicantListAsync(jobId))
        .unwrap()
        .catch(() => {
          toast({
            title: "Failed to load applicants",
            status: "error",
            duration: 2500,
            isClosable: true,
            position: "top-right",
          })
        })
        .finally(() => setIsLoading(false))
    }
  }, [dispatch, jobId, toast])

  const handleReject = async (uuid) => {
    try {
      await dispatch(updateJobStatusAsync({ uuid, newStatus: "rejected" })).unwrap()
      toast({ title: "Candidate rejected", status: "info", duration: 1800, isClosable: true, position: "top-right" })
      if (jobId) dispatch(fetchApplicantListAsync(jobId))
    } catch {
      toast({ title: "Failed to update status", status: "error", duration: 2200, isClosable: true, position: "top-right" })
    }
  }

  const excelHeaders = [
    { header: "Name", key: "candidate_name", width: 20 },
    { header: "Email", key: "candidate_email", width: 25 },
    { header: "Phone", key: "candidate_phone", width: 15 },
    { header: "Gender", key: "candidate_gender", width: 10 },
    { header: "Date of Birth", key: "candidate_dob", width: 15 },
    { header: "Experience (Years)", key: "candidate_experience_year", width: 18 },
    { header: "Experience (Months)", key: "candidate_experience_month", width: 18 },
    { header: "Work Status", key: "candidate_work_status", width: 15 },
    { header: "Current City", key: "candidate_current_city", width: 15 },
    { header: "Job Title", key: "candidate_title", width: 20 },
    { header: "Preferred Cities", key: "preferred_cities", width: 25 },
    { header: "Applied Date", key: "applied_at", width: 25 },
    { header: "Status", key: "status", width: 25 },
  ]

  const handleExportToExcel = async () => {
    setIsExporting(true)
    try {
      const result = await dispatch(fetchEmployerCandidateJobApplyExportbyJobUuidAsync(jobId)).unwrap()
      if (!result?.response || !Array.isArray(result.response)) throw new Error("No data available for export")

      const formattedData = result.response.map((candidate) => {
        const preferredCities = Array.isArray(candidate.candidate_preferred_city)
          ? candidate.candidate_preferred_city.map((city) => city.location?.title).filter(Boolean).join(", ")
          : "N/A"

        const formatDateField = (dateString) => {
          if (!dateString) return "N/A"
          try {
            return format(parseISO(dateString), "yyyy-MM-dd")
          } catch {
            return dateString
          }
        }

        return {
          candidate_name: candidate.candidate_name || "N/A",
          candidate_email: candidate.candidate_email || "N/A",
          candidate_phone: toStr(candidate.candidate_phone) || "N/A",
          candidate_gender: candidate.candidate_gender || "N/A",
          candidate_dob: formatDateField(candidate.candidate_dob),
          candidate_experience_year: candidate.candidate_experience_year ?? 0,
          candidate_experience_month: candidate.candidate_experience_month ?? 0,
          candidate_work_status: candidate.candidate_work_status || "N/A",
          candidate_current_city: candidate.candidate_current_city || "N/A",
          candidate_title: candidate.candidate_title || "N/A",
          preferred_cities: preferredCities,
          applied_at: formatDateField(candidate.applied_at),
          status: candidate.status || "N/A",
        }
      })

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("Applicants by job")
      worksheet.columns = excelHeaders

      const headerRow = worksheet.getRow(1)
      headerRow.font = { bold: true }
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF00" } }
      headerRow.alignment = { horizontal: "center" }
      headerRow.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }

      formattedData.forEach((candidate) => worksheet.addRow(candidate))
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } }
          if (rowNumber > 1) cell.alignment = { horizontal: "center" }
        })
      })

      const buffer = await workbook.xlsx.writeBuffer()
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `applicants-by-job-details-${new Date().toISOString().split("T")[0]}.xlsx`)

      toast({ title: "Export Successful", description: "Candidate data has been exported to Excel successfully.", status: "success", duration: 2500, isClosable: true, position: "top-right" })
    } catch (error) {
      toast({ title: "Export Failed", description: error.message || "Failed to export candidate data", status: "error", duration: 3500, isClosable: true, position: "top-right" })
    } finally {
      setIsExporting(false)
    }
  }

  const formatDateText = (dateString) => {
    if (!dateString) return "xxxxx"
    try {
      return format(parseISO(dateString), "MMM dd, yyyy")
    } catch {
      return "xxxxx"
    }
  }

  const getAge = (dob) => {
    if (!dob) return "xx"
    try {
      const birthDate = new Date(dob)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--
      return age
    } catch {
      return "xx"
    }
  }

  // Filtered/sorted list + quick search
  const visibleApplicants = useMemo(() => {
    const list = Array.isArray(applicants) ? [...applicants] : []
    const filtered = filterStatus ? list.filter((a) => (a?.status || "") === filterStatus) : list

    const q = toStr(query).trim().toLowerCase()
    const searched = q
      ? filtered.filter((a) => {
        const name = toStr(a?.candidate_name).toLowerCase()
        const email = toStr(a?.candidate_email).toLowerCase()
        const phone = toStr(a?.candidate_phone).toLowerCase()
        return name.includes(q) || email.includes(q) || phone.includes(q)
      })
      : filtered

    if (sortBy === "date") {
      return searched.sort((a, b) => {
        const da = new Date(a?.created_at || 0).getTime()
        const db = new Date(b?.created_at || 0).getTime()
        return db - da
      })
    }
    if (sortBy === "name") {
      return searched.sort((a, b) => toStr(a?.candidate_name).localeCompare(toStr(b?.candidate_name)))
    }
    return searched
  }, [applicants, filterStatus, sortBy, query])

  const renderSkeletons = () =>
    Array.from({ length: 4 }).map((_, index) => (
      <Box key={index} p={4} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm">
        <Flex mb={4}>
          <SkeletonCircle size="12" mr={3} />
          <Box flex="1">
            <SkeletonText mt={1} noOfLines={2} spacing={3} skeletonHeight={3} />
          </Box>
        </Flex>
        <SkeletonText mt={3} noOfLines={3} spacing={3} skeletonHeight={2} />
        <Flex mt={4} justifyContent="space-between">
          <Skeleton height="32px" width="100px" borderRadius="md" />
          <Skeleton height="32px" width="80px" borderRadius="md" />
        </Flex>
      </Box>
    ))

  return (
    <div className="">
      <div className="p-3 bg-mainBg">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          {/* Header with filters and export */}
          <div className="mb-6 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
            <div>
              <h2 className="mb-1 text-2xl font-semibold text-slate-800">Applicant List</h2>
              <p className="text-sm text-slate-500">
                {!isLoading && Array.isArray(applicants) ? `${applicants.length} candidates found for this position` : "Searching for qualified candidates..."}
              </p>
            </div>

            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
              <div className="w-full md:w-64">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />
                  </InputLeftElement>
                  <Input placeholder="Search by name, email or phone" value={query} onChange={(e) => setQuery(e.target.value)} bg="white" />
                </InputGroup>
              </div>

              <select
                aria-label="Filter by status"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-400 md:w-48"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Filter by Status</option>
                <option value="applied">Applied</option>
                <option value="shortlist">Shortlisted</option>
                <option value="interview_scheduled">Interview Scheduled</option>
                <option value="rejected">Rejected</option>
                <option value="hired">Hired</option>
              </select>

              <select
                aria-label="Sort by"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-400 md:w-44"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="">Sort by</option>
                <option value="date">Application Date</option>
                <option value="name">Name</option>
              </select>

              <button
                aria-label="Export to Excel"
                className="flex items-center rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-sm text-green-700 transition-colors duration-200 hover:border-green-300 hover:bg-green-100"
                onClick={handleExportToExcel}
                disabled={isExporting}
              >
                <FontAwesomeIcon icon={faFileExcel} className={`mr-2 ${isExporting ? "text-green-400" : "text-green-600"}`} />
                {isExporting ? "Exporting..." : "Export to Excel"}
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2">
            {isLoading
              ? renderSkeletons()
              : Array.isArray(visibleApplicants) && visibleApplicants.length > 0
                ? visibleApplicants.map((applicant) => {
                  const s = statusStyles(applicant?.status)
                  const prefSkills = Array.isArray(applicant?.candidate_preference_skills) ? applicant.candidate_preference_skills : []
                  return (
                    <div key={applicant?.uuid} className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
                      {/* Status indicator */}
                      <div className="absolute right-0 top-0 z-10">
                        <div className={`${s.badgeBg} rounded-bl-lg px-2.5 py-1`}>
                          <span className={`${s.badgeText} text-xs font-medium uppercase`}>{applicant?.status || "xxxxx"}</span>
                        </div>
                      </div>

                      {/* Header background */}
                      <div className="relative border-b border-slate-100 bg-slate-50 px-4 pb-8 pt-4">
                        {/* Applied date */}
                        <div className="absolute right-2 top-2 flex items-center text-xs text-slate-500">
                          <ClockIcon className="mr-1 h-3 w-3" />
                          <span>Applied {formatDateText(applicant?.created_at)}</span>
                        </div>

                        {/* Lock/Unlock indicator */}
                        <div className="absolute left-2 top-2">
                          {applicant?.is_candidate_unlock ? (
                            <Tooltip label="Candidate Unlocked" placement="top">
                              <span className="inline-flex rounded-full bg-green-50 p-1">
                                <LockOpenIcon className="h-3 w-3 text-green-600" />
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip label="Candidate Locked" placement="top">
                              <span className="inline-flex rounded-full bg-red-50 p-1">
                                <LockClosedIcon className="h-3 w-3 text-red-600" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Profile section */}
                      <div className="relative z-10 -mt-6 px-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-full border border-slate-100 bg-white p-0.5 shadow-sm transition-colors group-hover:border-slate-200">
                            {applicant?.candidate_profile_pic ? (
                              <Avatar size="md" name={toStr(applicant?.candidate_name)} src={applicant?.candidate_profile_pic} className="border border-slate-100" />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 p-1">
                                <UserCircleIcon className="h-8 w-8 text-slate-500" />
                              </div>
                            )}
                          </div>
                          <div className="pt-1">
                            <div className="flex items-start gap-1">
                              <h2 className="text-base font-medium text-slate-800">{toStr(applicant?.candidate_name) || "xxxxx"}</h2>
                              {applicant?.is_candidate_unlock && (
                                <Tooltip label="Verified Candidate" placement="top">
                                  <span>
                                    <CheckBadgeIcon className="h-4 w-4 text-slate-500" />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {applicant?.is_new_resume === 1 && (
                                <Badge colorScheme="green" variant="subtle" borderRadius="full" px={1.5} fontSize="2xs">New Resume</Badge>
                              )}
                              <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={1.5} fontSize="2xs">
                                ID: {applicant?.uuid ? applicant.uuid.substring(0, 8) : "xxxxx"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Apply note */}
                      {!!toStr(applicant?.apply_note) && (
                        <div className="px-4 pt-3">
                          <div className="flex items-start gap-1.5 rounded-md border border-slate-100 bg-slate-50 p-2">
                            <BriefcaseIcon className="h-4 w-4 flex-shrink-0 text-slate-600" />
                            <p className="text-sm text-slate-700">{toStr(applicant?.apply_note)}</p>
                          </div>
                        </div>
                      )}

                      {/* Contact + info */}
                      <div className="mt-2 px-4 py-3">
                        <div className="grid grid-cols-1 gap-2.5">
                          <Tooltip label="Email" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <EnvelopeIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="truncate text-sm text-slate-700">{toStr(applicant?.candidate_email) || "xxxxx"}</span>
                            </div>
                          </Tooltip>

                          <Tooltip label="Phone" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <PhoneIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                            </div>
                          </Tooltip>
                          <div className="ml-10 -mt-2 text-sm text-slate-700">{toStr(applicant?.candidate_phone) || "xxxxx"}</div>

                          <Tooltip label="City" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <MapPinIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="text-sm text-slate-700">{toStr(applicant?.candidate_city) || "xxxxx"}</span>
                            </div>
                          </Tooltip>

                          <Tooltip label="Full Address" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="flex-shrink-0 rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <MapPinIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="truncate text-sm text-slate-700">
                                {toStr(applicant?.candidate_address) || "xxxxx"}, {toStr(applicant?.candidate_city) || "xxxxx"},{" "}
                                {toStr(applicant?.candidate_state) || "xxxxx"} - {toStr(applicant?.candidate_pincode) || "xxxxx"}
                              </span>
                            </div>
                          </Tooltip>

                          <Tooltip label="Current Company" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <BuildingOffice2Icon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="text-sm text-slate-700">{toStr(applicant?.candidate_current_company) || "xxxxx"}</span>
                            </div>
                          </Tooltip>

                          <Tooltip label="Notice Period" hasArrow>
                            <div className="group/item flex items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                              <div className="rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                <BriefcaseIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                              <span className="text-sm text-slate-700">{toStr(applicant?.candidate_notice_period) || "xxxxx"}</span>
                            </div>
                          </Tooltip>

                          {/* Preferred Skills (limit to 8 for layout) */}
                          {!!prefSkills.length && (
                            <Tooltip label="Preferred Skills" hasArrow>
                              <div className="group/item flex flex-wrap items-center gap-2 rounded-md p-1 transition-colors hover:bg-slate-50">
                                <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1.5 transition-colors group-hover/item:bg-slate-200">
                                  <BriefcaseIcon className="h-3.5 w-3.5 text-slate-600" />
                                  <span className="text-sm text-slate-700">Preferred Skills:</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {prefSkills.slice(0, 8).map((skillObj) => (
                                    <Tooltip key={skillObj?.uuid} label={skillObj?.skill?.title} hasArrow>
                                      <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                        {toStr(skillObj?.skill?.title) || "Unknown"}
                                      </span>
                                    </Tooltip>
                                  ))}
                                  {prefSkills.length > 8 && (
                                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600">
                                      +{prefSkills.length - 8} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Personal details */}
                      <div className="px-4 pb-3">
                        <div className="grid grid-cols-2 gap-2">
                          <Tooltip label="Gender" hasArrow>
                            <div className="flex items-center gap-1.5 rounded-md bg-slate-50 p-1.5">
                              <UserIcon className="h-3.5 w-3.5 text-slate-500" />
                              <span className="text-xs text-slate-600">{toStr(applicant?.candidate_gender) || "xxxxx"}</span>
                            </div>
                          </Tooltip>

                          <Tooltip label="Date of Birth & Age" hasArrow>
                            <div className="flex items-center gap-1.5 rounded-md bg-slate-50 p-1.5">
                              <CakeIcon className="h-3.5 w-3.5 text-slate-500" />
                              <span className="text-xs text-slate-600">
                                {formatDateText(applicant?.candidate_dob)} ({getAge(applicant?.candidate_dob)} yrs)
                              </span>
                            </div>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                        {!applicant?.is_candidate_unlock ? (
                          <button
                            onClick={() => handleUnlockCandidate(applicant?.uuid)}
                            disabled={unlockStatus[applicant?.uuid] === "loading"}
                            className={`flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm transition duration-200 ${unlockStatus[applicant?.uuid] === "loading" ? "bg-slate-400" : "bg-slate-600 hover:bg-slate-700"
                              }`}
                          >
                            {unlockStatus[applicant?.uuid] === "loading" ? (
                              <>
                                <span className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                <span>Unlocking...</span>
                              </>
                            ) : (
                              <>
                                <LockOpenIcon className="h-4 w-4" />
                                <span>Unlock Candidate</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <div className="col-span-2">
                              <button
                                onClick={() => handleApplicantClick(applicant)}
                                className="flex w-full items-center justify-center gap-1.5 rounded-md bg-slate-700 px-3 py-2 text-sm text-white shadow-sm transition duration-200 hover:bg-slate-800"
                              >
                                <EyeIcon className="h-4 w-4" />
                                <span>View Details</span>
                              </button>
                            </div>

                            <button
                              onClick={async () => {
                                try {
                                  await dispatch(updateJobStatusAsync({ uuid: applicant?.uuid, newStatus: "shortlist" })).unwrap()
                                  toast({ title: "Candidate shortlisted", status: "success", duration: 1800, isClosable: true, position: "top-right" })
                                  if (jobId) dispatch(fetchApplicantListAsync(jobId))
                                } catch {
                                  toast({ title: "Failed to update status", status: "error", duration: 2200, isClosable: true, position: "top-right" })
                                }
                              }}
                              className="flex items-center justify-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm text-white shadow-sm transition duration-200 hover:bg-green-700"
                            >
                              <CheckBadgeIcon className="h-4 w-4" />
                              <span>Shortlist</span>
                            </button>

                            <button
                              onClick={() => handleOpenModal(applicant)}
                              className="flex items-center justify-center gap-1.5 rounded-md bg-slate-600 px-3 py-2 text-sm text-white shadow-sm transition duration-200 hover:bg-slate-700"
                            >
                              <CalendarDaysIcon className="h-4 w-4" />
                              <span>Schedule</span>
                            </button>

                            <div className="col-span-2 mt-1">
                              <button
                                onClick={() => handleReject(applicant?.uuid)}
                                className="w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-red-600 shadow-sm transition duration-200 hover:bg-red-50"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
                : (
                  <div className="col-span-2 flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
                    <DocumentTextIcon className="mb-3 h-12 w-12 text-slate-300" />
                    <p className="mb-1 text-lg font-medium text-slate-700">No Applicants Found</p>
                    <p className="max-w-md text-center text-sm text-slate-500">
                      There are no applicants for this job position yet. Check back later or consider promoting your job listing.
                    </p>
                  </div>
                )}
          </div>

          {/* Interview Modal */}
          {isModalVisible && selectedCandidate && <Modal candidate={selectedCandidate} onClose={handleCloseModal} />}
        </div>
      </div>
    </div>
  )
}

export default ApplicantListByJob